export const spreadExamples = [
  {
    pair: 'EURUSD',
    bid: 1.08542,
    ask: 1.08548,
    label: 'Major pair',
    description: 'The most liquid pair — tight spread during London session',
  },
  {
    pair: 'USDJPY',
    bid: 156.342,
    ask: 156.352,
    label: 'Cross pair',
    description: 'Popular among Asian traders — widens during Tokyo close',
  },
  {
    pair: 'AUDCAD',
    bid: 0.91234,
    ask: 0.91284,
    label: 'Minor pair',
    description: 'Lower liquidity — wider spread during off-market hours',
  },
  {
    pair: 'EURGBP',
    bid: 0.84210,
    ask: 0.84240,
    label: 'Cross pair',
    description: 'Moderate volume — spreads depend on London session overlap',
  },
  {
    pair: 'XAUUSD',
    bid: 2345.12,
    ask: 2345.42,
    label: 'Commodity',
    description: 'Gold — spread widens during geopolitical events',
  },
] as const;

export const spreadFactors = [
  {
    title: 'Market Volatility',
    tagline: 'The detonator — news makes spreads explode',
    description: 'News events don\'t widen spreads gradually — they detonate them. When NFP, CPI, or FOMC data hits, market makers pull every quote they can within milliseconds. Your 1.2 pip EURUSD spread becomes 8, 12, even 15 pips in under 3 seconds. That\'s not a markup — it\'s a liquidity vacuum. The market doesn\'t warn you; it simply charges you more for the same trade.',
    stat: '3–10×',
    statLabel: 'instant spread spike during major news',
    impactLevel: 5,
  },
  {
    title: 'Liquidity Depth',
    tagline: 'More banks quoting = tighter prices',
    description: 'Think of spread as the price of competition. EURUSD has 40+ tier-1 banks fighting for your order every millisecond — spreads stay razor-thin because no single bank can afford to quote wide. An exotic pair like USDTRY has maybe 5 active market makers. Fewer competitors means the gap between bid and ask naturally widens. It\'s supply and demand at the millisecond level.',
    stat: '40+',
    statLabel: 'tier-1 banks quoting major pairs simultaneously',
    impactLevel: 3,
  },
  {
    title: 'Trading Session',
    tagline: 'Forex is a relay race — know who\'s running',
    description: 'Your broker\'s platform shows the same spread at 3 AM and 3 PM, but the reality is wildly different. When London and New York are both asleep, only a fraction of normal liquidity providers are active. The tightest window — London/NY overlap (8:00–12:00 UTC) — can have spreads 3–5× tighter than the quietest hours. Even Friday close to Sunday open is essentially dead money.',
    stat: '3–5×',
    statLabel: 'spread difference between peak overlap and quiet hours',
    impactLevel: 4,
  },
  {
    title: 'Broker Pricing Model',
    tagline: 'Same market, different bill — who you trade with matters',
    description: 'Two brokers can show completely different spreads for EURUSD at the exact same second. An ECN broker passes the raw 0.1 pip interbank price and adds a transparent $7 commission. A market maker bakes a 1.5 pip markup into the price and calls it "commission-free." You always pay — the question is how much is visible and how much is hidden in the quote.',
    stat: '0.0–1.5',
    statLabel: 'pip range from raw ECN to market maker markup',
    impactLevel: 2,
  },
] as const;

export const scalpingImpact = {
  scalper: {
    tradesPerDay: 15,
    lots: 0.1,
    spread: 1.2,
    pipsPerTrade: 8,
    days: 20,
  },
  dayTrader: {
    tradesPerDay: 5,
    lots: 0.5,
    spread: 1.2,
    pipsPerTrade: 25,
    days: 20,
  },
  swingTrader: {
    tradesPerDay: 1,
    lots: 2.0,
    spread: 1.2,
    pipsPerTrade: 120,
    days: 20,
  },
} as const;

export const spreadTypes = [
  {
    type: 'Fixed Spread',
    broker: 'Market Maker',
    icon: 'ri-lock-line',
    spread: '1.5 – 3.0 pips',
    commission: 'None',
    execution: 'Dealing Desk',
    desc: 'The broker guarantees the spread never changes. Sounds safe, but you pay a premium for that certainty.',
    pros: ['Predictable cost per trade', 'No surprise during news', 'Good for beginners'],
    cons: ['Usually wider than market rate', 'Broker may reject your trade', 'Not ideal for scalping'],
    bestFor: 'Beginners who want cost certainty',
  },
  {
    type: 'Floating Spread',
    broker: 'Standard Account',
    icon: 'ri-bar-chart-line',
    spread: '0.3 – 2.5 pips',
    commission: 'None',
    execution: 'STP / Hybrid',
    desc: 'Spread moves with the market. Tight when calm, wider during news. Most common account type.',
    pros: ['Tight when market is calm', 'No extra commission', 'Widely available'],
    cons: ['Unpredictable during volatility', 'Can widen 5–10x suddenly', 'Harder to plan exact costs'],
    bestFor: 'Day traders who avoid news events',
  },
  {
    type: 'Raw Spread',
    broker: 'ECN / Pro',
    icon: 'ri-dashboard-line',
    spread: '0.0 – 0.5 pips',
    commission: '$3.5 – $7 per lot',
    execution: 'ECN / DMA',
    desc: 'You get the raw interbank price. Tiny spread, but commission applies. Total cost is often the lowest.',
    pros: ['True market price', 'Lowest total cost for volume', 'Best for scalping'],
    cons: ['Commission adds up fast', 'Minimum deposit often higher', 'Platform complexity'],
    bestFor: 'Scalpers and high-volume traders',
  },
] as const;

export const spreadTypeCostScenario = [
  {
    spreadCost: 20,
    commission: 0,
    total: 20,
    totalNote: 'You pay a fixed spread of 2.0 pips ($20 per lot). No extra commission.',
    breakEven: 'Raw Spread only beats Fixed after 0 trades — it never does. You always pay the premium.',
  },
  {
    spreadCost: 12,
    commission: 0,
    total: 12,
    totalNote: 'You pay a floating spread of 1.2 pips ($12 per lot). No commission on standard accounts.',
    breakEven: 'Raw Spread catches up after 40 trades per month. Below that, Floating is cheaper.',
  },
  {
    spreadCost: 1,
    commission: 7,
    total: 8,
    totalNote: 'Raw spread of 0.1 pips ($1) + $7 commission = $8 total. Lowest total cost.',
    breakEven: 'Raw Spread is cheaper if you trade more than 40 lots per month. Otherwise Floating is better.',
  },
] as const;

export const sessionSpreads = [
  {
    hour: '00:00', label: 'Sydney open', eurusd: 1.8, gbpusd: 2.4, usdjpy: 1.2, active: false,
    session: 'Asia-Pacific',
    markets: 'Sydney opens. Tokyo prepares for morning session.',
    detail: 'Sydney kickstarts the trading week (or day). Liquidity is thin — mostly AUD and NZD pairs active. EUR and GBP pairs suffer from wide spreads as European market makers are offline.',
    eurusdCost: 18.0, gbpusdCost: 24.0, usdjpyCost: 12.0,
    recommendation: 'Avoid EUR/GBP pairs. AUDUSD and NZDUSD are viable.',
    rating: 'poor',
  },
  {
    hour: '02:00', label: 'Tokyo active', eurusd: 1.5, gbpusd: 2.0, usdjpy: 0.8, active: false,
    session: 'Asia-Pacific',
    markets: 'Tokyo in full swing. JPY crosses most active.',
    detail: 'Tokyo session brings JPY liquidity. USDJPY spreads tighten noticeably. EUR and GBP pairs start to improve but still wider than European hours.',
    eurusdCost: 15.0, gbpusdCost: 20.0, usdjpyCost: 8.0,
    recommendation: 'Good time for JPY crosses. EURUSD still not optimal.',
    rating: 'fair',
  },
  {
    hour: '04:00', label: 'Tokyo peak', eurusd: 1.2, gbpusd: 1.6, usdjpy: 0.6, active: false,
    session: 'Asia-Pacific',
    markets: 'Tokyo at peak volume. Singapore and Hong Kong active.',
    detail: 'Peak Asian liquidity. USDJPY spreads are at their daytime tightest. Asian indices and JPY pairs see the most action. European pairs still waiting for London.',
    eurusdCost: 12.0, gbpusdCost: 16.0, usdjpyCost: 6.0,
    recommendation: 'Best window for Asian session traders. Stick to JPY and AUD pairs.',
    rating: 'fair',
  },
  {
    hour: '06:00', label: 'London pre-open', eurusd: 1.0, gbpusd: 1.3, usdjpy: 0.9, active: false,
    session: 'Session transition',
    markets: 'London desks warming up. Frankfurt opens.',
    detail: 'European banks begin quoting. Spreads on EUR and GBP pairs tighten rapidly as liquidity builds. This is the transition from Asian to European dominance.',
    eurusdCost: 10.0, gbpusdCost: 13.0, usdjpyCost: 9.0,
    recommendation: 'Spreads improving fast. Good entry point before London officially opens.',
    rating: 'good',
  },
  {
    hour: '08:00', label: 'London opens', eurusd: 0.8, gbpusd: 1.0, usdjpy: 1.0, active: false,
    session: 'European',
    markets: 'London officially opens. Major European centers active.',
    detail: 'The deepest FX hub comes online. EURUSD and GBPUSD spreads drop below 1.0 pip. Volatility picks up as institutional orders flood the market.',
    eurusdCost: 8.0, gbpusdCost: 10.0, usdjpyCost: 10.0,
    recommendation: 'Excellent liquidity. Most pairs tradeable with tight pricing.',
    rating: 'good',
  },
  {
    hour: '10:00', label: 'London-NY overlap', eurusd: 0.5, gbpusd: 0.7, usdjpy: 0.7, active: true,
    session: 'London-New York overlap',
    markets: 'Both London and New York active. Peak global liquidity.',
    detail: 'This is the golden window. Two largest financial centers overlapping creates the deepest liquidity pool on earth. Spreads are at their absolute tightest across all major pairs. Institutional flow is at its maximum.',
    eurusdCost: 5.0, gbpusdCost: 7.0, usdjpyCost: 7.0,
    recommendation: 'Best time to trade. Tightest spreads of the day. All strategies viable.',
    rating: 'excellent',
  },
  {
    hour: '12:00', label: 'NY opens', eurusd: 0.6, gbpusd: 0.8, usdjpy: 0.8, active: true,
    session: 'New York',
    markets: 'New York fully operational. London still active.',
    detail: 'NY session brings fresh liquidity. US economic data releases happen in this window. Spreads remain very tight but expect volatility spikes around 8:30 and 10:00 AM ET releases.',
    eurusdCost: 6.0, gbpusdCost: 8.0, usdjpyCost: 8.0,
    recommendation: 'Great liquidity but watch for news events. Slippage risk around data releases.',
    rating: 'excellent',
  },
  {
    hour: '14:00', label: 'NY active', eurusd: 0.7, gbpusd: 1.0, usdjpy: 0.9, active: false,
    session: 'New York',
    markets: 'New York mid-session. London begins winding down.',
    detail: 'London desks start closing positions. Liquidity remains healthy from NY side but European flow fades. Spreads begin their gradual widening trend.',
    eurusdCost: 7.0, gbpusdCost: 10.0, usdjpyCost: 9.0,
    recommendation: 'Still tradeable. Expect spreads to gradually widen as London exits.',
    rating: 'good',
  },
  {
    hour: '16:00', label: 'NY afternoon', eurusd: 1.0, gbpusd: 1.4, usdjpy: 1.1, active: false,
    session: 'Session transition',
    markets: 'London closed. NY afternoon lull.',
    detail: 'European markets are fully closed. NY volume drops as traders take lunch. Spreads widen noticeably. This is a common time for range-bound price action with wider spreads.',
    eurusdCost: 10.0, gbpusdCost: 14.0, usdjpyCost: 11.0,
    recommendation: 'Avoid scalping here. Spreads are rising. Better to wait for Asia.',
    rating: 'fair',
  },
  {
    hour: '18:00', label: 'London close', eurusd: 1.4, gbpusd: 1.8, usdjpy: 1.3, active: false,
    session: 'Asia-Pacific transition',
    markets: 'NY winding down. Tokyo preparing to open.',
    detail: 'Lowest liquidity point between NY close and Tokyo open. Spreads are wide across the board. Weekend gap risk if it is Friday.',
    eurusdCost: 14.0, gbpusdCost: 18.0, usdjpyCost: 13.0,
    recommendation: 'Not recommended. Widest spreads outside weekend. Wait for Tokyo.',
    rating: 'poor',
  },
  {
    hour: '20:00', label: 'NY close', eurusd: 1.6, gbpusd: 2.2, usdjpy: 1.5, active: false,
    session: 'Asia-Pacific',
    markets: 'NY closed. Wellington and Sydney early.',
    detail: 'Lightest volume of the day. Only Wellington and early Sydney provide any liquidity. Spreads remain elevated. Thin markets prone to erratic price moves.',
    eurusdCost: 16.0, gbpusdCost: 22.0, usdjpyCost: 15.0,
    recommendation: 'Avoid unless trading NZD pairs. Risk of stop-hunting in thin markets.',
    rating: 'poor',
  },
  {
    hour: '22:00', label: 'Asia quiet', eurusd: 2.0, gbpusd: 2.8, usdjpy: 1.0, active: false,
    session: 'Asia-Pacific',
    markets: 'Pre-Tokyo lull. Sydney the only active center.',
    detail: 'Quietest stretch of the day. Only Sydney is open. JPY spreads are the tightest of the three but still elevated. European and US pairs are expensive to trade.',
    eurusdCost: 20.0, gbpusdCost: 28.0, usdjpyCost: 10.0,
    recommendation: 'Only JPY crosses make sense here. Best to wait for Tokyo to open.',
    rating: 'poor',
  },
] as const;

export const spreadFAQs = [
  {
    question: 'What is a "good" spread for EURUSD?',
    answer: 'On a standard account, 0.8–1.2 pips during London/NY hours is normal and fair. Anything under 0.6 pips on a commission-free account is excellent. On ECN/raw accounts, 0.1–0.3 pips plus commission is standard. If you see 2.0+ pips on EURUSD during active hours, you are overpaying — switch brokers or check your account type.',
    category: 'Basics',
  },
  {
    question: 'Can I avoid paying spread entirely?',
    answer: 'No. Spread is the fundamental cost of trading forex. Every single broker — market maker, ECN, or STP — builds spread into the price you see. Some brokers show it as a separate number (raw spread + commission), others embed it in the bid-ask gap. Either way, you pay it. The only way to avoid spread is not to trade.',
    category: 'Basics',
  },
  {
    question: 'Which pairs have the tightest spreads?',
    answer: 'EURUSD almost always has the tightest spread, often 0.5–1.0 pip on standard accounts. USDJPY and GBPUSD are close behind at 0.8–1.5 pips. Crosses like EURGBP run 1.0–2.0 pips. Exotic pairs like USDTRY, USDMXN, and EURHUF can have 20–100+ pip spreads because they lack liquidity and fewer market makers quote them. Gold (XAUUSD) typically runs 0.20–0.50 on ECN accounts.',
    category: 'Basics',
  },
  {
    question: 'Can spreads be negative?',
    answer: 'Technically yes — it is called an inverted spread — but it is extremely rare and lasts for milliseconds. It happens when a market maker misprices their quote or during extreme volatility when bid briefly exceeds ask. Retail traders cannot realistically profit from this; institutional algorithms sweep these arbitrage opportunities in under 5 milliseconds. If your broker shows a negative spread, it is almost certainly a data feed error.',
    category: 'Basics',
  },
  {
    question: 'Is a 0.0 pip spread real?',
    answer: 'Yes, but it is raw ECN pricing with commission added. The 0.0 pip is the spread between buy and sell from the interbank market. The broker then charges $3.50–$7.00 per lot as commission. The total cost is often lower than a 2-pip spread account, but only if you trade enough volume to justify the commission structure. Do the math: 0.0 pip + $7 commission vs 1.2 pips ($12) commission-free — the raw account wins at any volume.',
    category: 'Broker Differences',
  },
  {
    question: 'How do fixed spreads actually work?',
    answer: 'A market maker broker guarantees a minimum spread regardless of market conditions. They achieve this by acting as the counterparty to your trade rather than passing it to the interbank market. This means: (1) the fixed spread is always wider than the raw market spread — you pay a premium for the guarantee, (2) during news events, the broker may reject your order or requote you rather than honor the fixed spread, and (3) fixed spread accounts are rarely true ECN — the broker is your counterparty, creating a potential conflict of interest.',
    category: 'Broker Differences',
  },
  {
    question: 'How to verify a broker\'s real spreads before opening an account?',
    answer: 'Do not trust the marketing page. Open a demo account and watch the spread during three specific windows: (1) London open (8:00 UTC), (2) a major news event like NFP, and (3) the Asian session lull (20:00 UTC). Compare the demo spread to the advertised spread. Most reputable brokers also publish historical spread data or provide a spread monitor tool. If a broker refuses to offer a demo account or hides live spread data, walk away.',
    category: 'Broker Differences',
  },
  {
    question: 'How do I calculate spread cost in dollars?',
    answer: 'Spread cost = (spread in pips) × (lot size) × (pip value). For a standard lot of EURUSD at 1.2 pips: 1.2 × 1.0 × $10 = $12.00 per trade. For a mini lot (0.1): 1.2 × 0.1 × $10 = $1.20. The pip value varies by pair: EURUSD is $10/standard lot, USDJPY is about $9, GBPUSD is $10. Cross pairs have different pip values — always check before calculating. This cost is paid immediately upon entry, before the market moves at all.',
    category: 'Cost & Calculation',
  },
  {
    question: 'Is spread the same for all account types at the same broker?',
    answer: 'No. Most brokers offer at least two account tiers. A standard account might have a 1.2 pip EURUSD spread with no commission. A pro/ECN account from the same broker might show 0.1 pip but charge $7 per lot. For low-volume traders (< 20 lots/month), the standard account is usually cheaper. For high-volume traders, the raw account wins. Always run the numbers for your specific trading frequency before choosing an account type.',
    category: 'Cost & Calculation',
  },
  {
    question: 'Why does my spread widen right after I open a trade?',
    answer: 'This is usually because of timing. You may have entered during a news event, session transition, or low-liquidity period. The quote you saw on the platform was from a few milliseconds ago — by the time your order reaches the market, conditions have changed. This is called slippage. Use limit orders instead of market orders to control your entry price. Also, avoid trading during known high-volatility windows like NFP (first Friday of each month at 8:30 AM ET).',
    category: 'Trading Practical',
  },
  {
    question: 'Do spreads change during rollover?',
    answer: 'Yes — spreads typically widen for 5–15 minutes around the 5 PM ET (22:00 UTC) rollover. This is when brokers settle open positions for the next trading day and liquidity providers briefly pull their quotes. The spread on EURUSD can jump from 0.8 pips to 2–4 pips during rollover. If you hold positions overnight, avoid opening or closing trades in this window. Most experienced traders plan entries and exits at least 15 minutes before or after rollover.',
    category: 'Trading Practical',
  },
  {
    question: 'What happens to spreads during weekend gaps?',
    answer: 'When the market reopens on Sunday (22:00 UTC), the opening spread can be 5–10× wider than normal. This is because the bid-ask gap must absorb any price movement that occurred over the weekend. If a geopolitical event happened between Friday close and Sunday open, the spread will reflect the uncertainty. Many brokers also widen spreads deliberately during the first 15–30 minutes of the trading week to protect themselves from gap risk. Smart traders wait at least 30 minutes after Sunday open before entering positions.',
    category: 'Trading Practical',
  },
] as const;

export const relatedSpreadGuides = [
  {
    title: 'Commission',
    href: '/forex-commission/',
    desc: 'ECN brokers charge a commission per lot — here\'s exactly how much it costs and how the math works. It\'s simpler than you think.',
    icon: 'ri-bank-card-line',
  },
  {
    title: 'Swap / Rollover',
    href: '/forex-swap/',
    desc: 'Holding a trade past midnight means you\'re either earning or paying interest. Learn when swap quietly eats into your profit the most.',
    icon: 'ri-moon-clear-line',
  },
  {
    title: 'Slippage',
    href: '/forex-slippage/',
    desc: 'You click buy, but the fill price is worse than you expected. That\'s slippage — here\'s what triggers it and how to keep it under control.',
    icon: 'ri-speed-up-line',
  },
  {
    title: 'Hidden Charges',
    href: '/hidden-forex-trading-costs/',
    desc: 'Conversion markups, withdrawal fees, idle account charges — the stuff you won\'t find on your trade ticket but your broker still takes.',
    icon: 'ri-eye-off-line',
  },
] as const;